import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import { useInView } from 'react-intersection-observer'
import styled, { createGlobalStyle } from 'styled-components'

import LineCropIcon from 'LuxKit/Icons/line/LineCropIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import focusStyle from 'styles/utils/focusStyle'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import { breakpointSizes } from 'components/utils/breakpoint'
import loadable from 'components/utils/lazyImport'
import { MapboxProvider, useMapboxContext } from 'contexts/MapboxContext'
import ModalContext from 'contexts/ModalContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { getImageUrl } from 'lib/image/imageUtils'
import { clamp } from 'lib/maths/mathUtils'
import { MAPBOX_ACCESS_TOKEN_USE_CASES } from 'lib/maps/mapboxAccessToken'

import {
  MAPBOX_STATIC_IMAGE_BASE_URL,
  MAPBOX_STATIC_IMAGE_MAX_DIMENSION,
  MAPBOX_STATIC_IMAGE_MIN_DIMENSION,
  PROPERTY_MAP_CONTROL_BUTTON_SIZE,
  PROPERTY_MAP_CONTROL_ICON_SIZE,
  PROPERTY_MAP_COORDINATE_PRECISION,
  PROPERTY_MAP_DEFAULT_STATIC_IMAGE_HEIGHT,
  PROPERTY_MAP_DEFAULT_STATIC_MARKER_IMAGE_ID,
  PROPERTY_MAP_SINGLE_MARKER_ZOOM,
  PROPERTY_MAP_STATIC_AUTO_VIEWPORT_PADDING,
  PROPERTY_MAP_STATIC_IN_VIEW_ROOT_MARGIN,
  PROPERTY_MAP_STATIC_MARKER_DEFAULT_DIMENSIONS,
  PROPERTY_MAP_STATIC_MARKER_IMAGE_DIMENSIONS_BY_ID,
} from './constants'
import { getMarkerPin } from './markerPins'
import type { PropertyMapProps } from './types'

interface StaticPropertyMapProps extends PropertyMapProps {
  height: number
  modalTitle: string
  // Called once when the static map preview scrolls near the viewport.
  onScrolledIntoView?: () => void
}

interface FullScreenMapModalProps extends PropertyMapProps {
  modalTitle: string
}

interface StaticMapUrlOptions {
  accessToken: string
  focusOnFirstMarker?: boolean
  height: number
  highlightedMarkerId?: string
  markers: Array<App.MapMarker>
}

const MODAL_MAP_HEIGHT = '100%'
const MODAL_MAP_WRAPPER_STYLE: React.CSSProperties = { height: MODAL_MAP_HEIGHT }

const InteractivePropertyMap = loadable<PropertyMapProps>(
  () => import(/* webpackChunkName: "PropertyMap" */ 'components/Common/PropertyMap/PropertyMap'),
)

const FullscreenModalStyle = createGlobalStyle`
  div[data-static-property-map-fullscreen].size-max {
    max-width: 100% !important;
    max-height: 100% !important;
    border-radius: 0 !important;
  }
`

const StaticMapRoot = styled.div`
  width: 100%;
`

const StaticMapButton = styled.button`
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
  background: ${props => props.theme.palette.neutral.default.seven};
  cursor: pointer;

  &:focus-visible {
    ${focusStyle}
  }
`

const FullscreenButtonOverlay = styled.div`
  position: absolute;
  top: ${rem(8)};
  right: ${rem(8)};
  display: flex;
  width: ${rem(PROPERTY_MAP_CONTROL_BUTTON_SIZE)};
  height: ${rem(PROPERTY_MAP_CONTROL_BUTTON_SIZE)};
  border-radius: ${props => props.theme.borderRadius.S};
  background: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: ${props => props.theme.shadow.flat.small};
  pointer-events: none;
`

const FullscreenButtonOverlayIcon = styled(LineCropIcon)`
  margin: auto;
  color: ${props => props.theme.palette.neutral.default.one};
`

const StaticMapImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const StaticMapPicture = styled.picture`
  display: block;
  width: 100%;
  height: 100%;
`

const StaticMapPlaceholder = styled(LoadingBox)`
  width: 100%;
  height: 100%;
`

const ModalMapBody = styled(ModalBody)`
  min-height: 0;
`

const ModalMapContent = styled(ModalContent)`
  display: flex;
  min-height: 0;
  padding: 0;
`

const ModalMapContainer = styled.div`
  flex: 1;
  min-height: 0;
  height: 100%;
`

function formatCoordinate(value: number) {
  return Number(value.toFixed(PROPERTY_MAP_COORDINATE_PRECISION)).toString()
}

function getStaticMarkerImageId(marker: App.MapMarker) {
  const markerPin = getMarkerPin(marker.type)
  const markerImageId = markerPin.imageId ?? PROPERTY_MAP_DEFAULT_STATIC_MARKER_IMAGE_ID

  return markerImageId
}

function getStaticMarker(marker: App.MapMarker, highlightedMarkerId?: string) {
  const isActive = highlightedMarkerId === marker.id
  const markerImageId = getStaticMarkerImageId(marker)
  const markerImageDimensions = PROPERTY_MAP_STATIC_MARKER_IMAGE_DIMENSIONS_BY_ID[markerImageId] ?? PROPERTY_MAP_STATIC_MARKER_DEFAULT_DIMENSIONS
  const markerImageUrl = getImageUrl(isActive ? `${markerImageId}-active` : markerImageId, {
    width: Math.round(markerImageDimensions.width),
    height: Math.round(markerImageDimensions.height),
  })

  return `url-${encodeURIComponent(markerImageUrl)}(${formatCoordinate(marker.longitude)},${formatCoordinate(marker.latitude)})`
}

function getStaticMapOverlay(markers: Array<App.MapMarker>, highlightedMarkerId?: string) {
  return markers.map(marker => getStaticMarker(marker, highlightedMarkerId)).join(',')
}

function getStaticMapViewport(markers: Array<App.MapMarker>, singleMarkerZoom: number, focusOnFirstMarker?: boolean) {
  const firstMarker = markers[0]
  if (!firstMarker) {
    return undefined
  }

  if (focusOnFirstMarker || markers.length === 1) {
    return `${formatCoordinate(firstMarker.longitude)},${formatCoordinate(firstMarker.latitude)},${singleMarkerZoom}`
  }

  return 'auto'
}

function buildStaticMapUrls({
  accessToken,
  focusOnFirstMarker,
  height,
  highlightedMarkerId,
  markers,
}: StaticMapUrlOptions) {
  // Utility function to return valid dimensions for the static image
  function normaliseStaticImageDimension(value: number) {
    return clamp(MAPBOX_STATIC_IMAGE_MIN_DIMENSION, Math.round(value), MAPBOX_STATIC_IMAGE_MAX_DIMENSION)
  }

  // get a URL safe string representing the markes to be displayed on the static map
  const overlay = getStaticMapOverlay(markers, highlightedMarkerId)
  // get a URL safe string representing focus on a given marker or focus the map to show all markers
  const viewport = getStaticMapViewport(markers, PROPERTY_MAP_SINGLE_MARKER_ZOOM, focusOnFirstMarker)

  if (!overlay || !viewport) {
    return undefined
  }

  const safeImageHeight = normaliseStaticImageDimension(Number.isFinite(height) ? height : PROPERTY_MAP_DEFAULT_STATIC_IMAGE_HEIGHT)
  const searchParams = new URLSearchParams({ access_token: accessToken })

  if (viewport === 'auto') {
    searchParams.set('padding', PROPERTY_MAP_STATIC_AUTO_VIEWPORT_PADDING.toString())
  }

  function buildStaticMapUrl(width: number) {
    const safeImageWidth = normaliseStaticImageDimension(width)

    return `${MAPBOX_STATIC_IMAGE_BASE_URL}/${overlay}/${viewport}/${safeImageWidth}x${safeImageHeight}?${searchParams.toString()}`
  }

  return {
    mobileUrl: buildStaticMapUrl(Math.round(breakpointSizes.mobile.max)),
    tabletUrl: buildStaticMapUrl(Math.round(breakpointSizes.tablet.max)),
    desktopUrl: buildStaticMapUrl(Math.round(breakpointSizes.desktop.max)),
    largeDesktopUrl: buildStaticMapUrl(Math.round(breakpointSizes.largeDesktop.max)),
  }
}

function FullScreenMapModal(props: FullScreenMapModalProps) {
  const {
    className,
    disablePanOnMarkerSelect,
    isSnapBackToOfferEnabled = true,
    focusOnFirstMarker,
    highlightedMarkerId,
    markerOptions,
    markers,
    mapboxAccessTokenUseCase,
    mapLoadingElement,
    mapOptions,
    modalTitle,
    multipleMarkers,
    onDrag,
    onSelectMarker,
    renderInfoWindow,
    showActiveMarkerState,
  } = props
  const modalElementContext = useModalElementContext()
  const modalMapOptions = useMemo(() => ({
    ...mapOptions,
    fullscreenControl: false,
  }), [mapOptions])
  const handleBackButtonClick = useCallback(() => {
    modalElementContext.resolve()
  }, [modalElementContext])

  return (
    <>
      <FullscreenModalStyle />
      <ModalBase
        size="max"
        height="full"
        data-static-property-map-fullscreen
      >
        <ModalHeader
          title={modalTitle}
          dismissible={false}
          onBackButtonClick={handleBackButtonClick}
        />
        <ModalMapBody>
          <ModalMapContent>
            <ModalMapContainer>
              <InteractivePropertyMap
                className={className}
                disablePanOnMarkerSelect={disablePanOnMarkerSelect}
                enableNearbyMarkerFullscreen={false}
                isSnapBackToOfferEnabled={isSnapBackToOfferEnabled}
                focusOnFirstMarker={focusOnFirstMarker}
                height={MODAL_MAP_HEIGHT}
                highlightedMarkerId={highlightedMarkerId}
                markerOptions={markerOptions}
                markers={markers}
                mapboxAccessTokenUseCase={mapboxAccessTokenUseCase}
                mapLoadingElement={mapLoadingElement}
                mapOptions={modalMapOptions}
                multipleMarkers={multipleMarkers}
                showActiveMarkerState={showActiveMarkerState}
                onDrag={onDrag}
                onSelectMarker={onSelectMarker}
                renderInfoWindow={renderInfoWindow}
                wrapperStyle={MODAL_MAP_WRAPPER_STYLE}
              />
            </ModalMapContainer>
          </ModalMapContent>
        </ModalMapBody>
      </ModalBase>
    </>
  )
}

function StaticPropertyMapContent(props: StaticPropertyMapProps) {
  const {
    className,
    disablePanOnMarkerSelect,
    isSnapBackToOfferEnabled,
    focusOnFirstMarker,
    height,
    highlightedMarkerId,
    markerOptions,
    markers,
    mapboxAccessTokenUseCase = MAPBOX_ACCESS_TOKEN_USE_CASES.property,
    mapLoadingElement,
    mapOptions,
    modalTitle,
    multipleMarkers,
    onDrag,
    onScrolledIntoView,
    onSelectMarker,
    renderInfoWindow,
    showActiveMarkerState,
    wrapperStyle,
  } = props
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const { accessToken } = useMapboxContext()
  const hasFiredScrolledIntoViewRef = useRef(false)
  const [inViewRef, hasScrolledIntoView] = useInView({
    rootMargin: PROPERTY_MAP_STATIC_IN_VIEW_ROOT_MARGIN,
    triggerOnce: true,
  })
  // Keep height out of styled-components to avoid generating dynamic classes for per-instance map sizes.
  const rootStyle = useMemo<React.CSSProperties>(
    () => ({
      height,
      ...wrapperStyle,
    }),
    [height, wrapperStyle],
  )

  const openMapLabel = intl.formatMessage({
    id: 'MY6qvf8O8nwMyrM8PJ_Z',
    defaultMessage: 'Open interactive map',
    description: 'Accessibility label for opening the fullscreen interactive property map',
  })

  const handleOpenMap = useCallback(() => {
    showModal(
      <FullScreenMapModal
        className={className}
        disablePanOnMarkerSelect={disablePanOnMarkerSelect}
        isSnapBackToOfferEnabled={isSnapBackToOfferEnabled}
        focusOnFirstMarker={focusOnFirstMarker}
        height={height}
        highlightedMarkerId={highlightedMarkerId}
        markerOptions={markerOptions}
        markers={markers}
        mapboxAccessTokenUseCase={mapboxAccessTokenUseCase}
        mapLoadingElement={mapLoadingElement}
        mapOptions={mapOptions}
        modalTitle={modalTitle}
        multipleMarkers={multipleMarkers}
        showActiveMarkerState={showActiveMarkerState}
        onDrag={onDrag}
        onSelectMarker={onSelectMarker}
        renderInfoWindow={renderInfoWindow}
        wrapperStyle={wrapperStyle}
      />,
    )
  }, [
    className,
    disablePanOnMarkerSelect,
    isSnapBackToOfferEnabled,
    focusOnFirstMarker,
    height,
    highlightedMarkerId,
    markerOptions,
    markers,
    mapboxAccessTokenUseCase,
    mapLoadingElement,
    mapOptions,
    modalTitle,
    multipleMarkers,
    onDrag,
    onSelectMarker,
    renderInfoWindow,
    showActiveMarkerState,
    showModal,
    wrapperStyle,
  ])

  useEffect(() => {
    if (!hasScrolledIntoView || !onScrolledIntoView || hasFiredScrolledIntoViewRef.current) {
      return
    }

    hasFiredScrolledIntoViewRef.current = true
    onScrolledIntoView()
  }, [hasScrolledIntoView, onScrolledIntoView])

  const staticMapUrls = useMemo(() => {
    if (!accessToken) {
      return undefined
    }

    return buildStaticMapUrls({
      accessToken,
      focusOnFirstMarker,
      height,
      highlightedMarkerId,
      markers,
    })
  }, [
    accessToken,
    focusOnFirstMarker,
    height,
    highlightedMarkerId,
    markers,
  ])
  const staticMapContent = staticMapUrls && hasScrolledIntoView ? (
    <StaticMapPicture>
      <source
        media={`(max-width: ${breakpointSizes.mobile.max}px)`}
        srcSet={staticMapUrls.mobileUrl}
      />
      <source
        media={`(max-width: ${breakpointSizes.tablet.max}px)`}
        srcSet={staticMapUrls.tabletUrl}
      />
      <source
        media={`(max-width: ${breakpointSizes.desktop.max}px)`}
        srcSet={staticMapUrls.desktopUrl}
      />
      <StaticMapImage
        alt=""
        src={staticMapUrls.largeDesktopUrl}
        loading="lazy"
        decoding="async"
        draggable={false}
      />
    </StaticMapPicture>
  ) : (
    <StaticMapPlaceholder
      data-testid="static-property-map-placeholder"
      borderRadius="S"
    />
  )
  const showFullscreenButtonOverlay = !!staticMapUrls && hasScrolledIntoView

  return (
    <StaticMapRoot
      ref={inViewRef}
      className={className}
      style={rootStyle}
    >
      <StaticMapButton
        type="button"
        aria-label={openMapLabel}
        onClick={handleOpenMap}
      >
        {staticMapContent}
        {showFullscreenButtonOverlay && (
          <FullscreenButtonOverlay
            aria-hidden="true"
            data-testid="static-property-map-fullscreen-overlay"
          >
            <FullscreenButtonOverlayIcon size={PROPERTY_MAP_CONTROL_ICON_SIZE} />
          </FullscreenButtonOverlay>
        )}
      </StaticMapButton>
    </StaticMapRoot>
  )
}

function StaticPropertyMap(props: StaticPropertyMapProps) {
  const {
    mapboxAccessTokenUseCase = MAPBOX_ACCESS_TOKEN_USE_CASES.property,
  } = props

  return (
    <MapboxProvider useCase={mapboxAccessTokenUseCase}>
      <StaticPropertyMapContent
        {...props}
        mapboxAccessTokenUseCase={mapboxAccessTokenUseCase}
      />
    </MapboxProvider>
  )
}

export default React.memo(StaticPropertyMap)
